/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import CardComponent from "../components/CardComponent";
import BookingLoginReservationsTable from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationsTable";
import BookingLoginTableHead from "pages/Front/Booking/BookingLogin/components/BookingLoginTableHead";
import BookingLoginReservationsTableBody
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationsTableBody";
import BookingLoginTableRow from "pages/Front/Booking/BookingLogin/components/BookingLoginTableRow";
import Dates from "utils/Dates";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import BookingLoginTableHeadColumn from "pages/Front/Booking/BookingLogin/components/BookingLoginTableHeadColumn";
import BookingLoginTableRowColumnResponsive
    from "pages/Front/Booking/BookingLogin/components/BookingLoginTableRowColumnResponsive";
import {Reservation} from "types/entities/Reservation";
import {consoleLog, padMinutes} from "utils/utility";
import {useDispatch} from "useDispatch";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import {sortReservationsByDate, useReservationStatusForLogin} from "utils/reservationUtils";
import SessionAction from "store/session/SessionAction";
import HallAction from "store/hall/HallAction";
import {DEFAULT_HALL_ID} from "helpers/config";
import {ThunkAction} from "redux-thunk";
import BookingAction from "store/booking/BookingAction";
import BookingLoginTableRowLink from "pages/Front/Booking/BookingLogin/components/BookingLoginTableRowLink";
import FeatherIcon from "components/FeatherIcon";
import clsx from "clsx";
import button from "components/Button";
import {QrCodeIcon} from "@heroicons/react/24/solid";
import {InfoMessageType, setInfoMessage} from "components/InfoMessage";
import BookingLoginZuumeHeader
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";

interface BookingLoginReservationsProps {}

const BookingLoginReservations = (props: BookingLoginReservationsProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const reservations = useTypedSelector(state => state.bookingLoginState.reservations);
    const session = useTypedSelector(state => state.sessionState.session);
    const [validReservation, setValidReservations] = useState<Reservation[]>([]);
    const {historyPush} = useNavigation();
    const {getStatus} = useReservationStatusForLogin();

    const setValidReservationsHandler = () => {
        setValidReservations(reservations);
    };

    const fetchReservationsListHandler = () => {
        dispatch(BookingLoginAction.fetchReservationsList())
            .then(
                onResolve => {},
                onReject => {
                    consoleLog(onReject);
                }
            )
            .catch(err => {
                consoleLog(err);
            });
    };
    const logoutHandler = () => {
        dispatch(BookingLoginAction.fetchReservationsLogout())
            .then(r => {
                dispatch(SessionAction.loginVisitor());
                dispatch(HallAction.fetchHall(DEFAULT_HALL_ID)).then(r => {});
                dispatch<ThunkAction<any, any, any, any>>(BookingAction.fetchActivities());
            })
            .finally(() => {
                dispatch(BookingLoginAction.setReservations([]));
                historyPush(RouteName.BookingLogin);
            });
    };

    const ReservationQRCard = (props: {reservation: Reservation}) => {
        if (props.reservation.qr_png == null) {
            return <></>;
        }
        return (
            <div className={"ReservationQRCard"}>
                <img src={props.reservation.qr_png} alt={"QR Code"} />
            </div>
        );
    };

    const showQRCodeHandler = (reservation: Reservation) => {
        console.log("showQRCodeHandler", reservation, reservation.qr_png);
        if (reservation.qr_png == null) {
            return;
        }

        setInfoMessage(t("Quick checkin QR code"), InfoMessageType.INFO, <ReservationQRCard reservation={reservation} />);
    };

    useEffect(() => {
        fetchReservationsListHandler();
        // }, [location.pathname]);
    }, []);

    useEffect(() => {
        setValidReservationsHandler();
    }, [reservations]);

    if (!validReservation) {
        return <Loader />;
    }

    return (
        <div className={"BookingLoginReservations w-full"}>
            <div className={"w-full text-left flex flex-row justify-between items-center mb-2"}>
                <BookingLoginZuumeHeader title={t("Your reservations")} />
                <button className={"px-5 text-sm py-3 inline-flex bg-black text-white rounded-xl"} onClick={logoutHandler}>
                    <span>{t("Logout")}</span>
                </button>
            </div>
            <CardComponent className={"w-full !bg-transparent md:!bg-white md:drop-shadow !p-0  overflow-hidden"}>
                <BookingLoginReservationsTable className={"overflow-x-auto min-w-full w-full"}>
                    <BookingLoginTableHead className={"min-w-full hidden md:grid grid-cols-6"}>
                        <BookingLoginTableHeadColumn className={"justify-center items-center"}>
                            <FeatherIcon name={"hash"} className={"size-5 mr-2 flex-none"} />
                            <span className={"flex shrink-0"}>{t("Booking ID")}</span>
                        </BookingLoginTableHeadColumn>
                        <BookingLoginTableHeadColumn className={"justify-center items-center"}>
                            <FeatherIcon name={"calendar"} className={"size-5 mr-2 flex-none"} />
                            <span className={"flex shrink-0"}>{t("Datum")}</span>
                        </BookingLoginTableHeadColumn>
                        <BookingLoginTableHeadColumn className={"justify-center items-center"}>
                            <FeatherIcon name={"clock"} className={"size-5 mr-2 flex-none"} />
                            <span className={"flex shrink-0"}>{t("Time")}</span>
                        </BookingLoginTableHeadColumn>
                        <BookingLoginTableHeadColumn className={"justify-center items-center"}>
                            <FeatherIcon name={"users"} className={"size-5 mr-2 flex-none"} />
                            <span className={"flex shrink-0"}>{t("Persons")}</span>
                        </BookingLoginTableHeadColumn>
                        <BookingLoginTableHeadColumn className={"justify-center items-center md:!col-span-2"}>
                            <FeatherIcon name={"edit"} className={"size-5 mr-2 flex-none"} />
                            <span className={"flex shrink-0"}>{t("Actions")}</span>
                        </BookingLoginTableHeadColumn>
                    </BookingLoginTableHead>
                    <BookingLoginReservationsTableBody className={"min-w-full"}>
                        {sortReservationsByDate(validReservation).map((reservation, index) => {
                            const reservationStatus = getStatus(reservation);

                            return (
                                <BookingLoginTableRow
                                    key={index}
                                    id={reservation.id}
                                    className={clsx(
                                        "text-[20px] font-medium border border-arbo-borderGray/50 md:border-0 md:border-b md:last-of-type:border-b-0 bg-white md:bg-transparent mb-3 shadow-card md:shadow-none md:mb-0 rounded-md md:rounded-none min-w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-6 p-0 md:py-3.5 md:px-1",
                                        !reservationStatus.value ? "text-[#878181]" : "text-black"
                                    )}
                                >
                                    <BookingLoginTableRowColumnResponsive
                                        className={"border-b md:border-b-0 border-dashed"}
                                        classNameValue={"md:justify-center"}
                                        label={t("Code")}
                                    >
                                        {reservation.guid}
                                    </BookingLoginTableRowColumnResponsive>
                                    <BookingLoginTableRowColumnResponsive
                                        className={"border-b md:border-b-0 border-dashed"}
                                        classNameValue={"md:justify-center"}
                                        label={t("Datum")}
                                    >
                                        {Dates.ymd(reservation.date, "DD.MM.YYYY")}
                                    </BookingLoginTableRowColumnResponsive>
                                    <BookingLoginTableRowColumnResponsive
                                        className={"border-b md:border-b-0 border-dashed"}
                                        classNameValue={"md:justify-center"}
                                        label={t("Time")}
                                    >
                                        {padMinutes(reservation.hour)}:{padMinutes(reservation.minute)}
                                    </BookingLoginTableRowColumnResponsive>

                                    <BookingLoginTableRowColumnResponsive
                                        className={"border-b md:border-b-0 border-dashed"}
                                        classNameValue={"md:justify-center"}
                                        label={t("Persons")}
                                    >
                                        {reservation.persons}
                                    </BookingLoginTableRowColumnResponsive>
                                    <BookingLoginTableRowColumnResponsive
                                        className={"col-span-1 sm:col-span-2 md:col-span-2 !p-0 md:!px-1.5 md:!py-1.5"}
                                        classNameValue={"justify-center md:justify-center gap-3 items-center px-2 md:px-0 py-2 md:py-0"}
                                    >
                                        <BookingLoginTableRowLink isNotDisabled={true} reservation={reservation} />
                                        {reservationStatus.name === "prepare" && (
                                            <button
                                                className={
                                                    "bg-white drop-shadow border border-gray-200 rounded-full size-12 flex-none flex items-center justify-center"
                                                }
                                                type="button"
                                                onClick={() => showQRCodeHandler(reservation)}
                                            >
                                                <QrCodeIcon className={"size-6 flex-none text-black"} />
                                            </button>
                                        )}
                                    </BookingLoginTableRowColumnResponsive>
                                </BookingLoginTableRow>
                            );
                        })}
                        {validReservation.length === 0 && <div className={"py-3 px-2 text-center"}>{t("No reservations were found")}</div>}
                    </BookingLoginReservationsTableBody>
                </BookingLoginReservationsTable>
            </CardComponent>
        </div>
    );
};

export default BookingLoginReservations;
